import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {

  /**
   * Mapea errores de la API a mensajes legibles para el usuario
   * @param error - Error recibido de la API
   * @param defaultMessage - Mensaje por defecto si no se puede extraer el mensaje del error
   * @returns Mensaje de error formateado
   */
  mapErrorMessage(error: any, defaultMessage: string = 'Ha ocurrido un error'): string {
    // Error de red o proxy (backend caido, status 0 o body no es JSON)
    if (error?.status === 0 || error?.error instanceof ProgressEvent) {
      return 'No pudimos conectar con el servidor. Verifica tu conexión e intenta de nuevo.';
    }

    // El servidor respondio con error (500+) pero no envio un cuerpo JSON con detalles.
    if (error?.status >= 500 && (error?.error == null || error?.error === '')) {
      return 'El servidor no está disponible en este momento. Intenta de nuevo más tarde.';
    }

    // Proxy devolvio error sin cuerpo JSON (ej: proxy sin upstream, HTML de error).
    if (typeof error?.error === 'string') {
      return 'No pudimos conectar con el servidor. Verifica tu conexión e intenta de nuevo.';
    }

    // Intenta extraer el mensaje del error en diferentes estructuras posibles.
    return error?.error?.error?.message ||
           error?.error?.message ||
           error?.message ||
           defaultMessage;
  }

  /**
   * Mapea errores especificos para el login
   * @param error - Error recibido de la API
   * @returns Mensaje de error para login
   */
  mapLoginError(error: any): string {
    return this.mapErrorMessage(error, 'Error al iniciar sesión');
  }

  /**
   * Mapea errores especificos para el registro
   * @param error - Error recibido de la API
   * @returns Mensaje de error para registro
   */
  mapRegisterError(error: any): string {
    return this.mapErrorMessage(error, 'Error al crear la cuenta');
  }

  /**
   * Mapea errores especificos para la recuperacion de contrasena
   * @param error - Error recibido de la API
   * @returns Mensaje de error para recuperacion de contrasena
   */
  mapForgotPasswordError(error: any): string {
    return this.mapErrorMessage(error, 'Error al validar el email');
  }
}

/**
 * Detects whether an API error (HttpErrorResponse or raw error) is about the
 * phone field and returns a user-facing message for it.
 *
 * Recognition rules:
 *  1. Strapi-style validation details: `error.error.details.errors[]` where an
 *     entry has `path: ['phone']` (or `path: 'phone'`).
 *  2. Any extracted message containing 'teléfono'/'telefono'/'phone'.
 *
 * Returns `null` when the error is not phone-related so callers can fall back
 * to the generic error mapping.
 */
export function extractPhoneFieldError(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;

  const detailMessage = findPhoneDetailMessage(
    (error as { error?: { details?: { errors?: unknown } } }).error?.details?.errors,
  );
  if (detailMessage) return detailMessage;

  const raw = error as {
    error?: { error?: { message?: unknown }; message?: unknown };
    message?: unknown;
  };
  const message = raw.error?.error?.message ?? raw.error?.message ?? raw.message;
  if (typeof message === 'string' && /tel[eé]fono|phone/i.test(message)) {
    return message;
  }

  return null;
}

/** True when a Strapi `path` (string or segment array) targets the phone field. */
function isPhonePath(path: unknown): boolean {
  const segments = Array.isArray(path) ? path : [path];
  return segments.some(
    (segment) => typeof segment === 'string' && segment.toLowerCase() === 'phone',
  );
}

/** Scans Strapi validation `details.errors[]` for the first phone-field entry message. */
function findPhoneDetailMessage(details: unknown): string | null {
  if (!Array.isArray(details)) return null;

  for (const detail of details) {
    if (!detail || typeof detail !== 'object') continue;
    const { path, message } = detail as { path?: unknown; message?: unknown };
    if (typeof message === 'string' && isPhonePath(path)) return message;
  }

  return null;
}